import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CpuChipIcon, 
  PlayIcon, 
  StopIcon, 
  TrashIcon,
  EyeIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { agentsService } from '@/services/agents';
import { Agent, CreateAgentRequest, UpdateAgentRequest } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import AgentCreationForm from '@/components/AgentCreation/AgentCreationForm';

interface AgentFilters {
  search: string;
  status: string;
  model: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface AgentDetailsModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  onToggleStatus: (agent: Agent) => void;
}

const AgentDetailsModal: React.FC<AgentDetailsModalProps> = ({ 
  agent, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete, 
  onToggleStatus 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />
        
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${agent.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                <CpuChipIcon className={`h-6 w-6 ${agent.is_active ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{agent.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Instructions</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{agent.instructions}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Tools</h4>
                <div className="flex flex-wrap gap-2">
                  {agent.tools && agent.tools.length > 0 ? (
                    agent.tools.map((tool) => (
                      <span
                        key={tool}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                      >
                        {tool}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">No tools assigned</span>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Model Settings</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="font-medium text-gray-900 dark:text-white">Model</dt>
                      <dd className="text-gray-700 dark:text-gray-300">{agent.model}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-900 dark:text-white">Temperature</dt>
                      <dd className="text-gray-700 dark:text-gray-300">{agent.model_settings.temperature || 'Default'}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-900 dark:text-white">Max Tokens</dt>
                      <dd className="text-gray-700 dark:text-gray-300">{agent.model_settings.max_tokens || 'Default'}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-900 dark:text-white">Parallel Tool Calls</dt>
                      <dd className="text-gray-700 dark:text-gray-300">{agent.model_settings.parallel_tool_calls ? 'Enabled' : 'Disabled'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {/* Metadata and Actions */}
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Status</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${agent.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Created: {new Date(agent.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Updated: {new Date(agent.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Usage Stats</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Usage Count</span>
                    <span className="font-medium text-gray-900 dark:text-white">{agent.usage_stats?.usage_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Success Rate</span>
                    <span className="font-medium text-gray-900 dark:text-white">{agent.usage_stats?.success_rate ? `${Math.round(agent.usage_stats.success_rate * 100)}%` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Avg Response Time</span>
                    <span className="font-medium text-gray-900 dark:text-white">{agent.usage_stats?.avg_response_time ? `${agent.usage_stats.avg_response_time}ms` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Actions</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => onEdit(agent)}
                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-blue-500 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Agent
                  </button>
                  <button
                    onClick={() => onToggleStatus(agent)}
                    className={`w-full flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                      agent.is_active
                        ? 'border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 focus:ring-yellow-500'
                        : 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 focus:ring-green-500'
                    }`}
                  >
                    {agent.is_active ? (
                      <>
                        <StopIcon className="h-4 w-4 mr-2" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-4 w-4 mr-2" />
                        Activate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => onDelete(agent)}
                    className="w-full flex items-center justify-center px-4 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 focus:ring-red-500 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Agent
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AgentEditModalProps {
  agent: Agent | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (agent: Agent, updates: any) => void;
}

const AgentEditModal: React.FC<AgentEditModalProps> = ({ 
  agent, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    model: 'gpt-4o',
    model_settings: {
      temperature: 0.7,
      max_tokens: 4000,
      parallel_tool_calls: true
    }
  });

  const [availableTools, setAvailableTools] = useState<string[]>([]);
  const [toolCategories, setToolCategories] = useState<Record<string, string[]>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [toolSearchQuery, setToolSearchQuery] = useState<string>('');
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load available tools and categories
  useEffect(() => {
    const loadTools = async () => {
      try {
        const [toolsResponse, categoriesResponse] = await Promise.all([
          agentsService.getAvailableTools(),
          agentsService.getToolsByCategory()
        ]);
        
        // Extract tool names from IntegrationTool objects
        const toolNames = toolsResponse.map(tool => tool.name);
        setAvailableTools(toolNames);
        
        // Set categories - convert IntegrationTool arrays to string arrays
        const categoriesWithToolNames: Record<string, string[]> = {};
        Object.entries(categoriesResponse).forEach(([category, tools]) => {
          categoriesWithToolNames[category] = tools.map(tool => tool.name);
        });
        setToolCategories(categoriesWithToolNames);
      } catch (error) {
        console.error('Error loading tools:', error);
        // Set fallback values with tools from seeded agents
        const fallbackTools = [
          'search_confluence_pages',
          'get_confluence_page', 
          'create_product_requirements_document',
          'link_confluence_page_to_jira_task',
          'create_technical_design_document',
          'get_jira_task',
          'get_github_repository',
          'get_github_repository_content',
          'create_implementation_branch',
          'commit_implementation_code',
          'create_implementation_pull_request'
        ];
        setAvailableTools(fallbackTools);
        setToolCategories({
          confluence: ['search_confluence_pages', 'get_confluence_page', 'create_product_requirements_document', 'link_confluence_page_to_jira_task', 'create_technical_design_document'],
          jira: ['get_jira_task'],
          github: ['get_github_repository', 'get_github_repository_content', 'create_implementation_branch', 'commit_implementation_code', 'create_implementation_pull_request']
        });
      }
    };

    loadTools();
  }, []);

  // Initialize form data when agent changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        description: agent.description || '',
        instructions: agent.instructions || '',
        model: agent.model || 'gpt-4o',
        model_settings: {
          temperature: agent.model_settings?.temperature || 0.7,
          max_tokens: agent.model_settings?.max_tokens || 4000,
          parallel_tool_calls: agent.model_settings?.parallel_tool_calls ?? true
        }
      });
      // Set selected tools from the agent's tools array
      setSelectedTools(agent.tools || []);
    }
  }, [agent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agent) return;

    setIsLoading(true);
    try {
      const updates = {
        ...formData,
        tools: selectedTools // Send as array directly
      };
      
      await onSave(agent, updates);
      onClose();
    } catch (error) {
      console.error('Error saving agent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToolToggle = (tool: string) => {
    setSelectedTools(prev => 
      prev.includes(tool) 
        ? prev.filter(t => t !== tool)
        : [...prev, tool]
    );
  };

  if (!isOpen || !agent) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />
        
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit Agent: {agent.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agent Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Model
                  </label>
                  <select
                    value={formData.model}
                    onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>
              </div>

              {/* Model Settings */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Temperature ({formData.model_settings.temperature})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={formData.model_settings.temperature}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model_settings: { 
                        ...prev.model_settings, 
                        temperature: parseFloat(e.target.value) 
                      } 
                    }))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Focused</span>
                    <span>Balanced</span>
                    <span>Creative</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={formData.model_settings.max_tokens}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model_settings: { 
                        ...prev.model_settings, 
                        max_tokens: parseInt(e.target.value) 
                      } 
                    }))}
                    min="1"
                    max="8000"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.model_settings.parallel_tool_calls}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        model_settings: { 
                          ...prev.model_settings, 
                          parallel_tool_calls: e.target.checked 
                        } 
                      }))}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Enable Parallel Tool Calls
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Instructions
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Enter detailed instructions for your agent..."
                required
              />
            </div>

            {/* Enhanced Tools Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Available Tools ({selectedTools.length} selected)
              </label>
              
              {/* Tool Search and Category Filter */}
              <div className="mb-3 space-y-2">
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search tools..."
                      value={toolSearchQuery}
                      onChange={(e) => setToolSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Categories</option>
                    {Object.keys(toolCategories).map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)} ({toolCategories[category]?.length || 0})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tool Selection */}
              <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                {Object.keys(toolCategories).length > 0 ? (
                  Object.entries(toolCategories)
                    .filter(([category]) => selectedCategory === 'all' || category === selectedCategory)
                    .map(([category, tools]) => {
                      const categoryTools = Array.isArray(tools) ? tools : [];
                      const filteredTools = categoryTools.filter((tool: string) =>
                        typeof tool === 'string' && 
                        tool.toLowerCase().includes(toolSearchQuery.toLowerCase())
                      );

                      if (filteredTools.length === 0) return null;

                      return (
                        <div key={category} className="p-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                              {category} Tools
                            </h4>
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const newSelected = [...selectedTools];
                                  filteredTools.forEach((tool: string) => {
                                    if (!newSelected.includes(tool)) {
                                      newSelected.push(tool);
                                    }
                                  });
                                  setSelectedTools(newSelected);
                                }}
                                className="text-xs px-2 py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedTools(prev => prev.filter(tool => !filteredTools.includes(tool)));
                                }}
                                className="text-xs px-2 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                            {filteredTools.map((tool: string) => (
                              <label key={tool} className="flex items-center space-x-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                <input
                                  type="checkbox"
                                  checked={selectedTools.includes(tool)}
                                  onChange={() => handleToolToggle(tool)}
                                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={tool}>
                                  {tool}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  // Fallback to simple list if categories not available
                  <div className="p-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {availableTools
                        .filter(tool => 
                          typeof tool === 'string' && 
                          tool.toLowerCase().includes(toolSearchQuery.toLowerCase())
                        )
                        .map(tool => (
                          <label key={tool} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={selectedTools.includes(tool)}
                              onChange={() => handleToolToggle(tool)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {tool}
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Tools Summary */}
              {selectedTools.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                  <div className="text-sm text-blue-800 dark:text-blue-300 mb-1">
                    Selected Tools ({selectedTools.length}):
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedTools.slice(0, 10).map(tool => (
                      <span
                        key={tool}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200"
                      >
                        {tool}
                        <button
                          type="button"
                          onClick={() => handleToolToggle(tool)}
                          className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {selectedTools.length > 10 && (
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        +{selectedTools.length - 10} more...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Agents: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getCurrentWorkspaceId, currentWorkspace, isLoading: isWorkspaceLoading } = useWorkspace();

  // Get current workspace ID
  const workspaceId = getCurrentWorkspaceId();

  // State management
  const [filters, setFilters] = useState<AgentFilters>({
    search: '',
    status: 'active', // Changed from 'all' to 'active' by default
    model: 'all',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // State for confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);

  // API queries - only run when workspace is ready
  const { data: agentsData, isLoading: isAgentsLoading, error, refetch } = useQuery({
    queryKey: ['agents', workspaceId, currentPage, pageSize, filters],
    queryFn: () => agentsService.getAgents({
      workspace_id: workspaceId!,
      page: currentPage,
      per_page: pageSize,
      search: filters.search || undefined,
      is_active: true // Always fetch only active agents
    }),
    enabled: isAuthenticated && !!workspaceId && !isWorkspaceLoading
  });

  // Combined loading state
  const isLoading = isWorkspaceLoading || isAgentsLoading;

  // Mutations
  const deleteAgentMutation = useMutation({
    mutationFn: agentsService.deleteAgent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflows', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Agent deleted successfully');
      setShowDetailsModal(false);
      setSelectedAgent(null);
      setShowDeleteConfirm(false);
      setAgentToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ agentId, isActive }: { agentId: number; isActive: boolean }) => 
      agentsService.toggleAgentStatus(agentId, !isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      toast.success('Agent status updated successfully');
      setShowDetailsModal(false);
      setSelectedAgent(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent status');
    }
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ agentId, updates }: { agentId: number; updates: UpdateAgentRequest }) => 
      agentsService.updateAgent(agentId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      toast.success('Agent updated successfully');
      setShowEditModal(false);
      setSelectedAgent(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    }
  });

  const createAgentMutation = useMutation({
    mutationFn: (data: CreateAgentRequest) => agentsService.createAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Agent created successfully');
      setShowCreateModal(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create agent');
    }
  });

  // Event handlers
  const handleDeleteAgent = (agent: Agent) => {
    if (agent.is_builtin) {
      toast.error('Cannot delete built-in agents. Built-in agents are system-managed.');
      return;
    }
    setAgentToDelete(agent);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAgent = () => {
    if (agentToDelete) {
      deleteAgentMutation.mutate(agentToDelete.id);
    }
  };

  const handleToggleStatus = (agent: Agent) => {
    if (agent.is_builtin) {
      toast.error('Cannot modify built-in agents. Built-in agents are system-managed.');
      return;
    }
    toggleStatusMutation.mutate({ agentId: agent.id, isActive: agent.is_active });
  };

  const duplicateAgentMutation = useMutation({
    mutationFn: (agent: Agent) => agentsService.duplicateAgent(agent.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Agent duplicated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to duplicate agent');
    }
  });

  const handleDuplicateAgent = (agent: Agent) => {
    duplicateAgentMutation.mutate(agent);
  };

  const handleViewAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setShowDetailsModal(true);
  };

  const handleEditAgent = (agent: Agent) => {
    if (agent.is_builtin) {
      toast.error('Cannot edit built-in agents. Built-in agents are system-managed.');
      return;
    }
    setSelectedAgent(agent);
    setShowEditModal(true);
    setShowDetailsModal(false);
  };

  const handleCreateAgent = (agent: Agent) => {
    // The AgentCreationForm has already created the agent using the mutation
    // Cache invalidation was handled by the createAgentMutation
    setShowCreateModal(false);
  };



  const getStatusColor = (agent: Agent) => {
    if (agent.is_active) {
      return 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400';
    }
    return 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-400';
  };

  const getStatusIcon = (agent: Agent) => {
    return agent.is_active ? (
      <CheckCircleIcon className="h-3 w-3 text-green-500" />
    ) : (
      <XCircleIcon className="h-3 w-3 text-gray-400" />
    );
  };

  const getModelColor = (model: string) => {
    const colors = {
      'gpt-4o': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      'gpt-4': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      'gpt-3.5-turbo': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    };
    return colors[model as keyof typeof colors] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  };

  const getBuiltinAgentBadge = (agent: Agent) => {
    if (!agent.is_builtin) return null;
    
    const badges = {
      'builtin': '🎭 Built-in',
      'system': '⚙️ System'
    };
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700">
        {badges[agent.agent_type as keyof typeof badges] || '🤖 Built-in'}
      </span>
    );
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      model: 'all',
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setCurrentPage(1);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Please log in to view agents</p>
        </div>
      </div>
    );
  }

  // Show loading state while workspace is being initialized
  if (isWorkspaceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Show error state if no workspace is available
  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No workspace selected</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Please select a workspace to view agents
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
        {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agents</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your AI agents and their configurations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Agent
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search agents..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>
          
          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          {/* Model Filter */}
          <div>
            <select
              value={filters.model}
              onChange={(e) => setFilters(prev => ({ ...prev, model: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Models</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>
          
          {/* Sort */}
          <div className="flex gap-2">
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="created_at">Created</option>
              <option value="name">Name</option>
              <option value="updated_at">Updated</option>
            </select>
            <button
              onClick={() => setFilters(prev => ({ 
                ...prev, 
                sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' 
              }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
            >
              {filters.sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={resetFilters}
              className="flex items-center px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Reset Filters
            </button>
            <button
              onClick={() => refetch()}
              className="flex items-center px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </button>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {agentsData?.agents.length || 0} agents found
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                      </div>
                    </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">Failed to load agents</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            {error.message.includes('Workspace ID') 
              ? 'Please ensure you have a workspace selected'
              : 'Please check your connection and try again'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : agentsData?.agents.length === 0 ? (
        <div className="text-center py-12">
          <CpuChipIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No agents found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {filters.search || filters.status !== 'all' || filters.model !== 'all'
              ? 'No agents match your current filters.'
              : 'Get started by creating your first agent.'}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Use the standard agent creation tools or contact your administrator.
          </p>
                      </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agentsData?.agents.map((agent) => (
            <div key={agent.id} className={`rounded-lg shadow-sm hover:shadow-md transition-shadow border ${
              agent.is_builtin 
                ? 'bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-indigo-200 dark:border-indigo-700' 
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                      agent.is_builtin 
                        ? 'bg-indigo-100 dark:bg-indigo-900/30' 
                        : agent.is_active 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <CpuChipIcon className={`h-6 w-6 ${
                        agent.is_builtin 
                          ? 'text-indigo-600 dark:text-indigo-400' 
                          : agent.is_active 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-gray-600 dark:text-gray-400'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{agent.name}</h3>
                        {getBuiltinAgentBadge(agent)}
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(agent)}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(agent)}`}>
                          {agent.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
                  {agent.description || 'No description provided'}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getModelColor(agent.model)}`}>
                    {agent.model}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {agent.tools && agent.tools.length} tools
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <span>Created {new Date(agent.created_at).toLocaleDateString()}</span>
                  <span>Usage: {agent.usage_stats?.usage_count || 0}</span>
                </div>

                <div className="flex items-center gap-2">
                      <button
                    onClick={() => handleViewAgent(agent)}
                    className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                      </button>
                      <button
                    onClick={() => handleEditAgent(agent)}
                    disabled={agent.is_builtin}
                    className={`flex-1 flex items-center justify-center px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                      agent.is_builtin
                        ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                      >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(agent)}
                    disabled={agent.is_builtin}
                    className={`p-2 rounded-md text-sm font-medium transition-colors ${
                      agent.is_builtin
                        ? 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                        : agent.is_active
                          ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/50'
                          : 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50'
                        }`}
                  >
                    {agent.is_active ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleDuplicateAgent(agent)}
                    className="p-2 rounded-md text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAgent(agent)}
                    disabled={agent.is_builtin}
                    className={`p-2 rounded-md transition-colors ${
                      agent.is_builtin
                        ? 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                        : 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50'
                    }`}
                      >
                    <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Pagination */}
      {agentsData && agentsData.pages > 1 && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-6 py-3 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {currentPage} of {agentsData.pages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(agentsData.pages, prev + 1))}
              disabled={currentPage === agentsData.pages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, agentsData.total)} of {agentsData.total} results
          </div>
      </div>
      )}

      {/* Modals */}
      {selectedAgent && (
        <AgentDetailsModal
          agent={selectedAgent}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAgent(null);
          }}
          onEdit={handleEditAgent}
          onDelete={handleDeleteAgent}
          onToggleStatus={handleToggleStatus}
        />
      )}

      {selectedAgent && (
        <AgentEditModal
          agent={selectedAgent}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAgent(null);
          }}
                     onSave={(agent, updates) => {
             updateAgentMutation.mutate({ agentId: agent.id, updates });
           }}
        />
      )}

      {/* Agent Creation Form */}
      <AgentCreationForm
        isOpen={showCreateModal}
        onSubmit={handleCreateAgent}
        onCancel={() => setShowCreateModal(false)}
        workspaceId={workspaceId || ''}
        createAgent={createAgentMutation.mutateAsync}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && agentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={() => setShowDeleteConfirm(false)} />
            
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm Deletion</h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete "{agentToDelete.name}"? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAgent}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents; 